import { useState, useEffect } from 'react';
import client from '../../api/client';
import betClient from '../../api/betClient';
import AiInsightCard from './AiInsightCard';


const inputCls = "px-3 py-1.5 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] text-[14px] text-[#111827] dark:text-[#e2e8f0] placeholder-[#9CA3AF] focus:outline-none focus:border-[#005BB3] dark:focus:border-[#3b82f6] transition-colors duration-150";

function StatCard({ label, primary, secondary }) {
  return (
    <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-3 py-2.5">
      <p className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6] mb-1" style={{ fontFamily: 'var(--font-sans)' }}>{label}</p>
      <p className="text-[15px] font-bold text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-mono)' }}>{primary}</p>
      {secondary && <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mt-0.5" style={{ fontFamily: 'var(--font-sans)' }}>{secondary}</p>}
    </div>
  );
}

const TABS = [
  { key: 'ats', label: 'ATS', tip: 'ats' },
  { key: 'ou', label: 'O/U', tip: 'overunder' },
];

export default function MatchupAnalysis({ sport }) {
  const [team1, setTeam1] = useState('');
  const [team2, setTeam2] = useState('');
  const [teams, setTeams] = useState([]);
  const [opponents, setOpponents] = useState([]);
  const [season, setSeason] = useState('');
  const [seasons, setSeasons] = useState([]);
  const [tab, setTab] = useState('ats');

  const [atsData, setAtsData] = useState(null);
  const [ouData, setOuData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch teams + seasons when sport changes
  useEffect(() => {
    setTeam1(''); setTeam2(''); setSeason('');
    setAtsData(null); setOuData(null);
    setOpponents([]);

    client.get(`/api/odds/${sport}`)
      .then((r) => {
        const names = new Set();
        for (const ev of r.data || []) {
          if (ev.home_team) names.add(ev.home_team);
          if (ev.away_team) names.add(ev.away_team);
        }
        if (names.size > 0) {
          setTeams([...names].sort());
        } else {
          client.get(`/api/patterns/teams?sport=${sport}`)
            .then((r2) => setTeams(r2.data?.teams || []))
            .catch(() => setTeams([]));
        }
      })
      .catch(() => {
        client.get(`/api/patterns/teams?sport=${sport}`)
          .then((r2) => setTeams(r2.data?.teams || []))
          .catch(() => setTeams([]));
      });

    client.get(`/api/patterns/seasons?sport=${sport}`)
      .then((r) => {
        const s = r.data?.seasons || [];
        setSeasons(s);
        if (s.length > 0) setSeason(s[0]);
      })
      .catch(() => setSeasons([]));
  }, [sport]);

  // Fetch opponents when team1 or season changes
  useEffect(() => {
    setTeam2('');
    setOpponents([]);
    setAtsData(null); setOuData(null);
    if (!team1) return;

    const params = `team=${encodeURIComponent(team1)}&sport=${sport}${season ? `&season=${season}` : ''}`;
    client.get(`/api/patterns/opponents?${params}`)
      .then((r) => setOpponents(r.data?.opponents || []))
      .catch(() => setOpponents([]));
  }, [team1, sport, season]);

  function fetchAll() {
    if (!team1 || !team2) return;
    setLoading(true); setError(null);
    setAtsData(null); setOuData(null);

    const params = new URLSearchParams({ team1, team2, sport });
    if (season) params.set('season', season);

    Promise.all([
      betClient.get(`/api/patterns/matchup/ats?${params.toString()}`),
      betClient.get(`/api/patterns/matchup/over-under?${params.toString()}`),
    ])
      .then(([ats, ou]) => { setAtsData(ats.data); setOuData(ou.data); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  const activeType = tab === 'ats' ? 'matchup_ats' : 'matchup_ou';
  const hasData = atsData || ouData;

  return (
    <div className="max-w-[1100px]">
      {/* Inputs */}
      <div className="flex items-end gap-2 mb-4">
        <div>
          <label className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] block mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Team 1</label>
          <select value={team1} onChange={(e) => setTeam1(e.target.value)} className={`w-[300px] ${inputCls}`}>
            <option value="">Select team...</option>
            {teams.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] block mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Team 2</label>
          <select value={team2} onChange={(e) => setTeam2(e.target.value)} disabled={!team1} className={`w-[300px] ${inputCls} disabled:opacity-50`}>
            <option value="">{team1 ? 'Select opponent...' : 'Pick Team 1 first'}</option>
            {opponents.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] block mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Season</label>
          <select value={season} onChange={(e) => setSeason(e.target.value)} className={`w-[150px] ${inputCls}`}>
            {seasons.length === 0 && <option value="">No data</option>}
            {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={fetchAll} disabled={loading || !team1 || !team2}
          className="px-4 py-1.5 rounded-md bg-[#005BB3] hover:bg-[#004494] disabled:opacity-40 text-white text-[14px] font-medium transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>
          {loading ? 'Loading...' : 'Compare'}
        </button>
      </div>

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 mb-3">
          <p className="text-[#DC2626] text-[12px]">Failed to load: {error}</p>
          <button onClick={fetchAll} className="text-[11px] text-[#DC2626] underline mt-1">Retry</button>
        </div>
      )}

      {!loading && !hasData && !error && (
        <div className="py-12 text-center">
          <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>Select two teams and click Compare to see head-to-head ATS and over/under results.</p>
        </div>
      )}

      {loading && (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <div key={i} className="h-16 rounded bg-[#F8F9FA] dark:bg-[#151a25] skeleton" />)}
        </div>
      )}

      {hasData && !loading && (
        <>
          {/* Underline tabs */}
          <div className="flex items-end gap-0 mb-4 border-b border-[#E5E7EB] dark:border-[#1e2536] pb-0">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-0 mr-6 pb-2 text-[14px] whitespace-nowrap transition-colors duration-150 border-b-2 ${
                  tab === t.key
                    ? 'font-semibold text-[#111827] dark:text-[#e2e8f0] border-[#005BB3] dark:border-[#3b82f6]'
                    : 'font-medium text-[#6B7280] dark:text-[#8892a8] border-transparent hover:text-[#374151] dark:hover:text-[#e2e8f0]'
                }`}
                style={{ fontFamily: 'var(--font-sans)' }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ATS */}
          {tab === 'ats' && atsData && (
            atsData.total_games === 0 ? <EmptyState /> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                <StatCard label={`${atsData.team1} ATS`}
                  primary={<RecordDisplay record={atsData[`${atsData.team1}_ats`]} />}
                  secondary={`${(atsData[`${atsData.team1}_ats`]?.pct * 100 || 0).toFixed(1)}%`}
                />
                <StatCard label={`${atsData.team2} ATS`}
                  primary={<RecordDisplay record={atsData[`${atsData.team2}_ats`]} />}
                  secondary={`${(atsData[`${atsData.team2}_ats`]?.pct * 100 || 0).toFixed(1)}%`}
                />
                <StatCard label="Games Played" primary={atsData.total_games} />
              </div>
            )
          )}

          {/* O/U */}
          {tab === 'ou' && ouData && (
            ouData.total_games === 0 ? <EmptyState /> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                <StatCard label="O/U Record"
                  primary={
                    <span style={{ fontFamily: 'var(--font-mono)' }}>
                      <span className="text-[#029456]">{ouData.record.overs}O</span>
                      {' - '}
                      <span className="text-[#DC2626]">{ouData.record.unders}U</span>
                      {ouData.record.pushes > 0 && <span className="text-[#6B7280]"> - {ouData.record.pushes}P</span>}
                    </span>
                  }
                  secondary={`${(ouData.record.over_pct * 100).toFixed(1)}% over rate`}
                />
                <StatCard label="Avg Total" primary={ouData.avg_total} secondary="combined points" />
                <StatCard label="Games Played" primary={ouData.total_games} />
              </div>
            )
          )}

          {/* Table + AI side by side */}
          {((tab === 'ats' && atsData?.total_games > 0) || (tab === 'ou' && ouData?.total_games > 0)) && (
            <div className="flex flex-col lg:flex-row gap-4 items-start mt-4">
              <div className="w-full lg:w-3/5 min-w-0">
                <MatchupTable
                  games={tab === 'ats' ? atsData.games : ouData.games}
                  type={tab}
                  t1={tab === 'ats' ? atsData.team1 : null}
                />
              </div>
              <div className="w-full lg:w-2/5 min-w-0">
                <AiInsightCard
                  key={`${activeType}-${team1}-${team2}-${sport}`}
                  analysisType={activeType}
                  params={{ team1, team2, sport, season: season || undefined }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


function RecordDisplay({ record }) {
  if (!record) return '-';
  return (
    <span className="text-[13px] font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>
      <span className="text-[#029456]">{record.wins}W</span>
      {' - '}
      <span className="text-[#DC2626]">{record.losses}L</span>
      {record.pushes > 0 && <span className="text-[#6B7280]"> - {record.pushes}P</span>}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-10">
      <p className="text-[#6B7280] dark:text-[#8892a8] text-[14px]" style={{ fontFamily: 'var(--font-sans)' }}>No head-to-head games found.</p>
      <p className="text-[#9CA3AF] dark:text-[#505a6e] text-[13px] mt-1" style={{ fontFamily: 'var(--font-sans)' }}>Try a different season.</p>
    </div>
  );
}

function MatchupTable({ games, type, t1 }) {
  return (
    <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] overflow-x-auto">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E7EB] dark:border-[#1e2536]">
        <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
        <h3 className="text-[12px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>Head-to-Head History</h3>
      </div>
      <table className="w-full text-left">
        <thead>
          <tr className="text-[12px] uppercase tracking-[0.5px] text-[#6B7280] border-b border-[#E5E7EB] dark:border-[#1e2536]">
            <th className="py-2 px-3 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Date</th>
            <th className="py-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Matchup</th>
            <th className="py-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Score</th>
            <th className="py-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>{type === 'ats' ? 'Spread' : 'Total'}</th>
            <th className="py-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Result</th>
          </tr>
        </thead>
        <tbody>
          {games.map((g, i) => {
            const date = new Date(g.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
            const spreadKey = t1 ? `${t1}_spread` : null;
            const resultKey = t1 ? `${t1}_ats_result` : null;
            const result = type === 'ats' ? g[resultKey]?.toUpperCase() : g.ou_result?.toUpperCase();
            const resultColor = type === 'ats'
              ? (g[resultKey] === 'win' ? 'text-[#029456]' : g[resultKey] === 'loss' ? 'text-[#DC2626]' : 'text-[#6B7280]')
              : (g.ou_result === 'over' ? 'text-[#029456]' : g.ou_result === 'under' ? 'text-[#DC2626]' : 'text-[#6B7280]');

            return (
              <tr key={g.event_id || i} className="border-b border-[#E5E7EB] dark:border-[#1e2536] text-[14px]">
                <td className="py-2 px-3 text-[#6B7280]" style={{ fontFamily: 'var(--font-mono)' }}>{date}</td>
                <td className="py-2 text-[#1F2937] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>{g.away_team} @ {g.home_team}</td>
                <td className="py-2 text-[#1F2937] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-mono)' }}>{g.score}</td>
                <td className="py-2 text-[#6B7280]" style={{ fontFamily: 'var(--font-mono)' }}>
                  {type === 'ats' && spreadKey && g[spreadKey] != null && (g[spreadKey] > 0 ? `+${g[spreadKey]}` : g[spreadKey])}
                  {type === 'ou' && g.closing_total != null && `O/U ${g.closing_total}`}
                </td>
                <td className={`py-2 font-semibold ${resultColor}`} style={{ fontFamily: 'var(--font-mono)' }}>{result}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
